import React, { useState, useEffect, useCallback } from "react";
import ListPagination, {
  LIST_DEFAULT_PAGE_SIZE,
  buildPageQuery,
  createPageChangeHandler,
  parseListFromPayload,
  parseTotalFromPayload,
} from "../../components/ListPagination";
import {
  Button,
  Input,
  InputNumber,
  Table,
  Modal,
  message,
  Spin,
} from "antd";
import API, {
  LEADERBOARD_RANKING,
  userFakeRankingUrl,
} from "../../network/api";
import { TEXT_DEF } from "../../constant/text";

const { confirm } = Modal;

const TEXT = {
  addFakeUser: "Thêm user fake",
  addFakePlaceholder: "Thêm tên user",
  predictPoints: "Số trận dự đoán",
  pointsPlaceholder: "Nhập số trận",
  save: "Lưu",
  accountName: "Tên tài khoản",
  actions: "Hành động",
  edit: "Sửa",
  delete: "Xóa",
  editPointsTitle: "Chỉnh sửa số trận dự đoán",
  usernameMustBeString: "Username must be a string",
  cancel: "Hủy",
  confirmDelete: "Bạn có chắc muốn xóa user này khỏi bảng xếp hạng?",
  fakeUserAlreadyExists: "User fake đã tồn tại",
  usernameIsRealCannotFake:
    "Username này là user thật, không thể tạo user fake",
  addFakeFailedGeneric: "Không thể thêm user fake.",
};

/** Ưu tiên correctPredictions (contract BE fake BXH) */
function pickPredictionScore(item) {
  if (item?.correctPredictions != null) return Number(item.correctPredictions);
  if (item?.points != null) return Number(item.points);
  if (item?.predictedPoints != null) return Number(item.predictedPoints);
  return 0;
}

function mapApiItem(item, index) {
  return {
    id: String(item?.id ?? item?._id ?? `row-${index}`),
    username: item?.username ?? item?.userName ?? "",
    correctPredictions: pickPredictionScore(item),
  };
}

function normalizeUsernameKey(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase();
}

/** Phân loại lỗi POST user-fake-rankings (theo status / message / code BE) */
function classifyAddFakeUserError(err) {
  const status = err?.response?.status;
  const d = err?.response?.data ?? {};
  const parts = [d.message, d.error, d.msg, d.description]
    .flat()
    .filter((x) => x != null && x !== "");
  const rawMsg = parts.map((x) => String(x)).join(" ");
  const m = rawMsg.toLowerCase();
  const code = String(d.code ?? d.errorCode ?? "").toLowerCase();

  const realHints =
    /user\s*thật|real\s*user|actual\s*user|là\s*user\s*thật|không\s*thể\s*tạo.*fake|cannot.*fake|is\s*real|real_user|existing.*real/i.test(
      rawMsg
    ) ||
    ["real_user", "user_is_real", "existing_real_user", "actual_user"].includes(
      code
    );

  if (realHints) {
    return "real_user";
  }

  if (
    status === 409 ||
    /duplicate|already\s*exist|đã\s*tồn\s*tại|unique\s*constraint/i.test(
      m
    ) ||
    ["duplicate", "already_exists", "user_exists"].includes(code)
  ) {
    return "duplicate_fake";
  }

  return "unknown";
}

const LeaderboardSettings = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);

  const onPageChange = createPageChangeHandler(page, pageSize, setPage, setPageSize);

  const [newUsername, setNewUsername] = useState("");
  const [newPoints, setNewPoints] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editPoints, setEditPoints] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const locale = { emptyText: TEXT_DEF.text0035 };

  const fetchList = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const { query } = buildPageQuery(pageNum, pageSize);
      const res = await API.get(`${LEADERBOARD_RANKING}?${query}`);
      const payload = res.data?.data ?? res.data;
      const list = parseListFromPayload(payload);
      setDataList(list.map((item, i) => mapApiItem(item, i)));
      setTotalRecords(parseTotalFromPayload(payload, list.length));
    } catch (err) {
      console.warn(err);
      setDataList([]);
      setTotalRecords(0);
      messageApi.error(
        err?.response?.data?.message || "Không tải được danh sách user fake."
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, messageApi]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleAdd = async () => {
    const u = newUsername?.trim();
    if (!u) {
      messageApi.error("Vui lòng nhập tên user");
      return;
    }
    if (newPoints == null || Number(newPoints) < 0) {
      messageApi.error("Vui lòng nhập số trận dự đoán hợp lệ");
      return;
    }
    const correctPredictions = Number(newPoints);
    const key = normalizeUsernameKey(u);
    if (
      dataList.some((row) => normalizeUsernameKey(row.username) === key)
    ) {
      messageApi.warning(TEXT.fakeUserAlreadyExists);
      return;
    }
    try {
      await API.post(LEADERBOARD_RANKING, {
        username: u,
        correctPredictions,
      });
      setNewUsername("");
      setNewPoints(null);
      messageApi.success("Đã thêm user fake.");
      setPage(1);
      await fetchList(1);
    } catch (err) {
      console.warn(err);
      const kind = classifyAddFakeUserError(err);
      if (kind === "duplicate_fake") {
        messageApi.warning(TEXT.fakeUserAlreadyExists);
        return;
      }
      if (kind === "real_user") {
        messageApi.warning(TEXT.usernameIsRealCannotFake);
        return;
      }
      const fallback =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        TEXT.addFakeFailedGeneric;
      messageApi.error(
        typeof fallback === "string" ? fallback : TEXT.addFakeFailedGeneric
      );
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    setEditPoints(record.correctPredictions);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    setEditPoints(null);
  };

  const submitEdit = async () => {
    if (!editing) return;
    const raw = editing.username;
    const usernameStr =
      typeof raw === "string" ? raw.trim() : raw != null ? String(raw).trim() : "";
    if (!usernameStr) {
      messageApi.error(TEXT.usernameMustBeString);
      return;
    }
    if (editPoints == null || Number(editPoints) < 0) {
      messageApi.error("Nhập số trận dự đoán hợp lệ");
      return;
    }
    const pts = Number(editPoints);
    setEditSaving(true);
    try {
      await API.patch(userFakeRankingUrl(editing.id), {
        username: usernameStr,
        correctPredictions: pts,
      });
      messageApi.success("Đã cập nhật số trận dự đoán.");
      closeEdit();
      await fetchList();
    } catch (err) {
      console.warn(err);
      messageApi.error(
        err?.response?.data?.message || "Cập nhật số trận dự đoán thất bại."
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: TEXT.confirmDelete,
      content: (
        <>
          <div>Tài khoản: <strong>{record.username}</strong></div>
        </>
      ),
      okText: TEXT.delete,
      okType: "danger",
      cancelText: TEXT.cancel,
      centered: true,
      onOk: async () => {
        try {
          await API.delete(userFakeRankingUrl(record.id));
          messageApi.success("Đã xóa.");
          await fetchList();
        } catch (err) {
          console.warn(err);
          messageApi.error(err?.response?.data?.message || "Xóa thất bại.");
          return Promise.reject(err);
        }
      },
    });
  };

  const columns = [
    {
      title: TEXT.accountName,
      dataIndex: "username",
      key: "username",
      align: "center",
    },
    {
      title: TEXT.predictPoints,
      dataIndex: "correctPredictions",
      key: "correctPredictions",
      align: "center",
      render: (v) => (v != null ? String(v) : "—"),
    },
    {
      title: TEXT.actions,
      key: "actions",
      align: "center",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <Button type="primary" size="small" onClick={() => openEdit(record)}>
            {TEXT.edit}
          </Button>
          <Button type="primary" danger size="small" onClick={() => handleDelete(record)}>
            {TEXT.delete}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="col-12" style={{ padding: "8px 0" }}>
      {contextHolder}

      <div style={{ marginBottom: 28, maxWidth: 960 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div
            style={{
              minWidth: 160,
              width: "20%",
              textAlign: "right",
              paddingRight: 16,
              fontWeight: 500,
            }}
          >
            {TEXT.addFakeUser}:
          </div>
          <div style={{ flex: "1 1 280px", maxWidth: 480 }}>
            <Input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder={TEXT.addFakePlaceholder}
              allowClear
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div
            style={{
              minWidth: 160,
              width: "20%",
              textAlign: "right",
              paddingRight: 16,
              fontWeight: 500,
            }}
          >
            {TEXT.predictPoints}:
          </div>
          <div style={{ flex: "1 1 280px", maxWidth: 480 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={newPoints}
              onChange={(v) => setNewPoints(v)}
              placeholder={TEXT.pointsPlaceholder}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 160, width: "20%", paddingRight: 16 }} aria-hidden />
          <div
            style={{
              flex: "1 1 280px",
              maxWidth: 480,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button type="primary" onClick={handleAdd}>
              {TEXT.save}
            </Button>
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataList}
          pagination={false}
          bordered
          locale={locale}
          style={{ width: "100%" }}
        />
        <ListPagination
          current={page}
          total={totalRecords}
          pageSize={pageSize}
          onChange={onPageChange}
          unit="tài khoản"
        />
      </Spin>

      <Modal
        title={TEXT.editPointsTitle}
        open={editOpen}
        onCancel={closeEdit}
        onOk={submitEdit}
        okText={TEXT.save}
        cancelText={TEXT.cancel}
        confirmLoading={editSaving}
        centered
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <strong>{editing?.username}</strong>
        </div>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          value={editPoints}
          onChange={(v) => setEditPoints(v)}
          placeholder={TEXT.pointsPlaceholder}
        />
      </Modal>
    </div>
  );
};

export default LeaderboardSettings;
